import { HttpClient } from '@angular/common/http';
import { Injectable, isDevMode } from '@angular/core';
import { DataStateChangeEventArgs } from '@syncfusion/ej2-angular-grids';
import { Observable, throwError, Subject } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { HttpHelper } from '../helpers/http-helper';
import { IEntity, ILLRevisionHistory, IServerSettings } from '../models/index';
import { AuthenticationService } from './authentication.service';
import { SettingsService } from './settings.service';
import { StorageManagerService } from './storage-manager.service';

@Injectable()
export class LCRFormService extends Subject<DataStateChangeEventArgs> {

    private action: string;
    private settings: IServerSettings;
    private apiUrl: string;
    apiResult: any;
    httpHelper: HttpHelper;

    constructor(private http: HttpClient, private settingsService: SettingsService, private authService: AuthenticationService, private storageManagerService: StorageManagerService) {
        super();

        // Define an instance of the http helper class that will be used to create httpHeader for api calls
        this.httpHelper = new HttpHelper(http, authService, storageManagerService);
    }

    // Get a list of LCR forms for the dashboard
    getLCRForms(): Observable<any[]> {
        const headers = this.httpHelper.createHttpHeaders();
        this.settings = this.settingsService.getSettings();
        this.apiUrl = this.settings.ApiGatewayUrl;
        this.action = "ssdpds/v1/LCRForms/LCRDashboard";

        return this.http.get(`${this.apiUrl}/${this.action}`, { headers: headers })
            .pipe(map(this.extractData),
              catchError(this.handleErrors));
    }

    // Get a list of LCR deliveries for the dashboard
    getLCRDelilveries(): Observable<any[]> {
        const headers = this.httpHelper.createHttpHeaders();
        this.settings = this.settingsService.getSettings();
        this.apiUrl = this.settings.ApiGatewayUrl;
        this.action = "ssdpds/v1/LCRForms/LCRDeliveryDashboard";

        return this.http.get(`${this.apiUrl}/${this.action}`, { headers: headers })
            .pipe(map(this.extractData),
              catchError(this.handleErrors));
    }

    // Get LCR form data by revisionid
    getLCRFormById(revisionId: string): Observable<IEntity[]> {
        const headers = this.httpHelper.createHttpHeaders();
        this.settings = this.settingsService.getSettings();
        this.apiUrl = this.settings.ApiGatewayUrl;
        this.action = `ssdpds/v1/LCRForms/${revisionId}`;

        return this.http.get(`${this.apiUrl}/${this.action}`, { headers: headers })
            .pipe(map(this.extractData),
              catchError(this.handleErrors));
    }

    // Get LCR form data by revisionid
    getLabelerCompanyContactInfo(revisionId: string): Observable<IEntity[]> {
        const headers = this.httpHelper.createHttpHeaders();
        this.settings = this.settingsService.getSettings();
        this.apiUrl = this.settings.ApiGatewayUrl;
        this.action = `ssdpds/v1/LLForms/${revisionId}`;

        return this.http.get(`${this.apiUrl}/${this.action}`, { headers: headers })
            .pipe(map(this.extractData),
              catchError(this.handleErrors));
    }

    // Get ssdp vacabularies
    getSSDPVocabularies(): Observable<IEntity[]> {
        const headers = this.httpHelper.createHttpHeaders();
        this.settings = this.settingsService.getSettings();
        this.apiUrl = this.settings.ApiGatewayUrl;
        this.action = 'ssdpds/v1/CommonData/Vocabularies';

        return this.http.get(`${this.apiUrl}/${this.action}`, { headers: headers })
            .pipe(map(this.extractData),
              catchError(this.handleErrors));
    }

    // Get revision history
    getRevisionHistory(revisionId: number): Observable<ILLRevisionHistory[]> {

        const headers = this.httpHelper.createHttpHeaders();
        this.settings = this.settingsService.getSettings();
        this.apiUrl = this.settings.ApiGatewayUrl;
        this.action = `ssdpds/v1/LCRForms/LCRVersionHistory/${revisionId}`;

        return this.http.get(`${this.apiUrl}/${this.action}`, { headers: headers })
            .pipe(map(this.extractData),
              catchError(this.handleErrors));
    }

    // Save LCR Form as a draft
    saveLCRForm(llData, modifiedByUser: string, draftMode: boolean) {
        let headers = this.httpHelper.createHttpHeaders();
        this.settings = this.settingsService.getSettings();
        this.apiUrl = this.settings.ApiGatewayUrl;
        this.action = `ssdpds/v1/LCRForms?isDraftMode=${draftMode}`;

        headers = headers.set('modifiedByUser', modifiedByUser);
        return this.http.post(`${this.apiUrl}/${this.action}`, llData, { headers: headers })
            .pipe(map(this.extractData),
              catchError(this.handleErrors));
    }

    // Get data successfully from api call
    private extractData(response: Response) {
        if (response.status < 200 || response.status >= 300) {
            throw new Error('Bad response status: ' + response.status);
        }

        const body = response;
        return body || {};
    }

    // Error handling
    private handleErrors(error: any): Observable<any> {
      const GENERIC_ERROR_MSG = 'It looks like something went wrong. Try reloading the page or contacting support if this issue continues.';
      const bDisplayDetailMsg = error && error.error && error.error.isBusinessErrorMessage ? true : isDevMode() ? true : false;
      error.error = error && error.error && error.error.message && bDisplayDetailMsg ? error.error.message : GENERIC_ERROR_MSG;

      return throwError(error.error);
    }
}
